import { Button } from "@/components/ui/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { Input } from "@/components/ui/Input";
import { useUpdateExchangeRate } from "@/hooks/services/use-exchange-rate";
import {
  updateAdminExchangeRateFormSchema,
  updateAdminExchangeRateFormValues,
} from "@/schemas/exchange-rate";
import { type AdminExchangeRate } from "@/services/core/admin-exchange-rate.api";
import { useDialogStore } from "@/store/dialog-store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

interface UpdateExchangeRateFormProps {
  selectedExchangeRate: AdminExchangeRate | null;
  setSelectedExchangeRate: React.Dispatch<
    React.SetStateAction<AdminExchangeRate | null>
  >;
  initialData: {
    id: string;
    amount: string;
  };
}

const UpdateExchangeRateForm = ({
  selectedExchangeRate,
  setSelectedExchangeRate,
  initialData,
}: UpdateExchangeRateFormProps) => {
  const updateMutation = useUpdateExchangeRate();
  const { closeUpdateDialog } = useDialogStore();
  const form = useForm<updateAdminExchangeRateFormValues>({
    resolver: zodResolver(updateAdminExchangeRateFormSchema),
    defaultValues: {
      id: initialData.id,
      amount: parseFloat(initialData.amount),
    },
  });

  useEffect(() => {
    form.reset({
      id: initialData.id,
      amount: parseFloat(initialData.amount)
    });
  }, [initialData]);

  const onSubmit = async (values: updateAdminExchangeRateFormValues) => {
    if (!selectedExchangeRate) return;

    const data = {
      nonce: "string",
      payload: {
        amount: values.amount,
      },
    };

    try {
      await updateMutation.mutateAsync({
        id: selectedExchangeRate.id,
        data,
      });
    } catch (error) {
      console.error("Failed to update exchange rate");
    }

    closeUpdateDialog();
    setSelectedExchangeRate(null);
  };
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Currency Code</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={selectedExchangeRate?.currencyCode || ""}
                  disabled
                  className="bg-muted"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Exchange Rate Amount</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  step="0.01"
                  placeholder="Enter exchange rate amount"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              closeUpdateDialog();
              setSelectedExchangeRate(null);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Updating..." : "Update Rate"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default UpdateExchangeRateForm;
